import turfBbox from '@turf/bbox'
import { FitBoundsOptions, LngLatBoundsLike, Map as MaplibreMap, MapGeoJSONFeature, MapMouseEvent } from 'maplibre-gl'
import { FeatureCollection, GeoJsonProperties, Geometry, Feature } from 'geojson'
import { ClusterType, MapLayer, MapSource } from './types'
import { getEmptyFeatureCollection } from '../../api-mappings/feature-collection'
import { getSource } from './utils'
import { MAX_ZOOM_FOR_CLUSTER_FORCED_ZOOM } from './constants'

/**
 * Returns true for clusters that have a type of Cluster, meaning the ones that are not yet expanded on the map.
 * @param feature The feature to check.
 * @returns True if this is a high-level cluster (one that hasn't been expanded yet). False otherwise.
 */
export function isNonExpandedCluster(feature: MapGeoJSONFeature) {
  return feature.properties['type'] === ClusterType.NonExpanded
}

/**
 * Returns the number of listings for a non-expanded cluster.
 * @param feature The feature to check.
 * @returns The number of listings for this cluster.
 */
export function nonExpandedClusterCount(feature: MapGeoJSONFeature) {
  return isNonExpandedCluster(feature) ? feature.properties['count'] : 0
}

/**
 * Sets the source data for a layer on the map.
 * @param map The map that has the layers.
 * @param source The source ID for the data.
 * @param data The new data to set.
 */
export function setSourceData(
  map: MaplibreMap | null,
  source: MapSource,
  data: FeatureCollection<Geometry, GeoJsonProperties> | undefined
) {
  const layerSource = getSource(map, source)
  if (data && data.features.length) {
    layerSource.setData(data)
  } else {
    layerSource.setData(getEmptyFeatureCollection())
  }
}

/**
 * Helper to set the bounds on the map. Needed because there is an issue with the typings for fitBounds.
 * @param map Instance of the maplibre Map to fit the bounds.
 * @param bounds Array containing the sw and ne coordinates of the bounds to set.
 * @param duration The animation time to fit the bounds.
 */
export function fitMapBounds(
  map?: MaplibreMap | null,
  bounds?: LngLatBoundsLike | null,
  options?: FitBoundsOptions | undefined
) {
  if (!map || !bounds) {
    return
  }

  map.fitBounds(bounds, options)
}

export function fitMapBoundsToFeature(map: MaplibreMap, feature: Feature, duration: number) {
  const bbox = turfBbox(feature)
  const sw = { lng: bbox[0], lat: bbox[1] }
  const ne = { lng: bbox[2], lat: bbox[3] }

  fitMapBounds(map, [sw, ne], { duration })
}

export function canZoomOnCluster(map: MaplibreMap, feature: MapGeoJSONFeature, layerId: MapLayer) {
  if (map.getZoom() > MAX_ZOOM_FOR_CLUSTER_FORCED_ZOOM) {
    return false
  }

  // We only support small cluster zoom on the original clusters (e.g. - non-expanded meaning the top level
  // clusters that are returned from the back end).
  if (!isNonExpandedCluster(feature)) {
    return false
  }

  // Have to be on the relevant layer.
  if ((feature.layer.id as MapLayer) !== layerId) {
    return false
  }

  return true
}

export function getClickedFeature(e: MapMouseEvent, layers: MapLayer[]): MapGeoJSONFeature | null {
  if (e.defaultPrevented || !e.target /* map */) {
    return null
  }

  const map = e.target
  const features = map.queryRenderedFeatures(e.point, { layers })
  if (!features?.length) {
    return null
  }

  // Only handle the top most feature that was clicked.
  return features[0]
}
